import { useState } from "react";
import { ChevronDown, ChevronRight, ThumbsDown, X } from "lucide-react";
import { toast } from "@/lib/toast";
import type { Camp, DebateMap } from "@/hooks/useFieldDebate";
import { usePlacementFeedback } from "@/hooks/useFieldDebate";

/** Slim paper info for title/score lookup — same shape the out-of-scope
 * listing derives from displayPapers (see analyze.tsx fdPaperList). */
export interface CampPaperInfo {
  id: string;
  title: string;
  normalized_score: number;
}

// Low-confidence placements sort first: grader overrides in the eval panels
// concentrated entirely on low/med-confidence rows, so those are the
// placements worth the user's skepticism (and their thumbs).
const CONF_RANK: Record<string, number> = { low: 0, med: 1, high: 2 };

function ConfidenceChip({ confidence }: { confidence: string }) {
  const cls =
    confidence === "low"
      ? "text-amber-400 border-amber-500/40"
      : confidence === "high"
        ? "text-emerald-400 border-emerald-500/40"
        : "text-muted-foreground border-border";
  return (
    <span
      className={`shrink-0 rounded border px-1 py-px text-[9px] font-medium uppercase tracking-wide ${cls}`}
    >
      {confidence}
    </span>
  );
}

interface Props {
  camp: Camp;
  conceptId: string;
  assignmentMeta?: DebateMap["assignment_meta"];
  papers?: CampPaperInfo[];
  onClose: () => void;
}

/** Camp drill-down: per-paper stance reasons + placement thumbs-down.
 * Sibling of UnalignedPanel/OutOfScopePanel in FieldDebateView's below-canvas
 * row; opens on camp click, replaced by clicking another camp. */
export function CampDrillDownPanel({
  camp,
  conceptId,
  assignmentMeta,
  papers,
  onClose,
}: Props) {
  const [open, setOpen] = useState(true);
  // Local one-shot state, not the query cache: "feedback given" is a UI
  // selection fact, and the map query refetching must not resurrect thumbs.
  const [flagged, setFlagged] = useState<Set<string>>(new Set());
  const feedback = usePlacementFeedback(conceptId);

  const byId = new Map((papers ?? []).map((p) => [p.id, p]));
  const rows = camp.paper_ids
    .map((pid) => {
      const meta = assignmentMeta?.[pid];
      const info = byId.get(pid);
      return {
        id: pid,
        title: info?.title ?? "",
        score: info?.normalized_score,
        confidence: meta?.confidence ?? "med",
        reason: meta?.reason ?? "",
      };
    })
    .sort(
      (a, b) =>
        (CONF_RANK[a.confidence] ?? 1) - (CONF_RANK[b.confidence] ?? 1) ||
        (b.score ?? 0) - (a.score ?? 0) ||
        (a.id < b.id ? -1 : 1),
    );

  const flag = (paperId: string) => {
    if (flagged.has(paperId)) return;
    // Optimistic mark, rolled back on error — server latency must not gate
    // the click feedback.
    setFlagged((prev) => new Set(prev).add(paperId));
    feedback.mutate(
      { paper_id: paperId, camp_id: camp.id },
      {
        onSuccess: () => toast.success("Placement feedback recorded"),
        onError: () => {
          setFlagged((prev) => {
            const next = new Set(prev);
            next.delete(paperId);
            return next;
          });
          toast.error("Couldn't record feedback — try again");
        },
      },
    );
  };

  return (
    <div className="w-[26rem] max-h-[40vh] flex flex-col bg-background/95 border border-border rounded-lg shadow-lg pointer-events-auto min-h-0">
      <div className="flex items-center justify-between px-3 py-2 shrink-0">
        <button
          type="button"
          onClick={() => setOpen((p) => !p)}
          className="flex items-center gap-1.5 text-muted-foreground text-xs font-semibold hover:text-foreground transition-colors min-w-0"
        >
          {open ? (
            <ChevronDown className="w-3 h-3 shrink-0" />
          ) : (
            <ChevronRight className="w-3 h-3 shrink-0" />
          )}
          {/* Count lives outside the truncating span — a long camp label must
              not eat the panel's only collapsed-state signal. */}
          <span className="truncate">{camp.label}</span>
          <span className="shrink-0">
            · {camp.paper_ids.length} placements
          </span>
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground shrink-0 ml-2"
          title="Close"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
      {open && (
        <div className="overflow-y-auto min-h-0">
          <div className="space-y-2.5 px-3 pb-3">
            {rows.map((row) => {
              const isFlagged = flagged.has(row.id);
              return (
                <div key={row.id} className="flex items-start gap-2 text-xs leading-snug">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="font-medium text-foreground truncate">
                        {row.title || `Paper ${row.id.slice(0, 8)}…`}
                      </span>
                      <ConfidenceChip confidence={row.confidence} />
                    </div>
                    {row.reason && (
                      <div className="text-muted-foreground">{row.reason}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => flag(row.id)}
                    disabled={isFlagged}
                    className={`mt-0.5 shrink-0 rounded p-0.5 transition-colors ${
                      isFlagged
                        ? "text-red-400/70 cursor-default"
                        : "text-muted-foreground/50 hover:text-red-400 hover:bg-red-500/10"
                    }`}
                    title={
                      isFlagged
                        ? "Flagged — thanks, this feeds assignment tuning"
                        : "Wrong camp? Flag this placement"
                    }
                  >
                    <ThumbsDown className="size-3" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
